import { defineStore } from "pinia";
import { db } from "@/firebase";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  query,
  getDocs,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { createPrefixedLogger } from "@/utils/logger";

const logger = createPrefixedLogger("DB");

export const useDBStore = defineStore("db", {
  state: () => ({}),

  actions: {
    /**
     * Get or create a user document using Firebase Auth UID
     * @param {string} uid - Firebase Auth UID
     * @param {object} userData - User data to create if document doesn't exist
     * @returns {object} User document data
     */
    async getOrCreateUser(uid, userData = null) {
      try {
        const userDoc = doc(db, "users", uid);
        const docSnap = await getDoc(userDoc);

        if (docSnap.exists()) {
          logger.debug("User found:", uid);
          return { id: uid, ...docSnap.data() };
        }

        if (userData) {
          logger.debug("Creating new user:", uid);
          await setDoc(userDoc, {
            ...userData,
            createdAt: new Date().toISOString(),
          });
          return { id: uid, ...userData };
        }

        logger.debug("User not found:", uid);
        return null;
      } catch (error) {
        logger.error("Error getting/creating user:", error);
        throw error;
      }
    },

    /**
     * Update user document by UID
     */
    async updateUser(uid, data) {
      try {
        const userDoc = doc(db, "users", uid);
        await updateDoc(userDoc, data);
        logger.debug("User updated:", uid);
        return true;
      } catch (error) {
        logger.error("Error updating user:", error);
        throw error;
      }
    },

    async addDocument(col, data, id) {
      try {
        if (id) {
          await setDoc(doc(db, col, id), data);
          logger.debug("Documento creado con ID:", id);
          return id;
        } else {
          const docRef = await addDoc(collection(db, col), data);
          logger.debug("Documento creado:", docRef.id);
          return docRef.id;
        }
      } catch (error) {
        logger.error("Error creando documento:", error);
        throw error;
      }
    },

    async deleteDocument(col, docId) {
      try {
        await deleteDoc(doc(db, col, docId));
        logger.debug("Documento eliminado:", docId);
        return true;
      } catch (error) {
        logger.error("Error eliminando documento:", error);
        throw error;
      }
    },

    async updateDocument(col, docId, data) {
      try {
        const docRef = doc(db, col, docId);
        await updateDoc(docRef, data);
        logger.debug("Documento actualizado:", docId);
        return true;
      } catch (error) {
        logger.error("Error actualizando documento:", error);
        throw error;
      }
    },

    async updateDocumentField(col, docId, field, value) {
      try {
        const docRef = doc(db, col, docId);
        await updateDoc(docRef, { [field]: value });
        logger.debug("Campo actualizado:", field);
        return true;
      } catch (error) {
        logger.error("Error actualizando campo:", error);
        throw error;
      }
    },

    async getDocument(col, docId) {
      try {
        logger.debug("Leyendo documento:", col, docId);
        const docSnap = await getDoc(doc(db, col, docId));

        if (docSnap.exists()) {
          return docSnap.data();
        } else {
          logger.debug("Documento no existe:", docId);
          return {};
        }
      } catch (error) {
        logger.error("Error leyendo documento:", error);
        throw error;
      }
    },

    async getDocuments(col, q) {
      try {
        logger.debug("Leyendo coleccion:", col);
        const colRef = collection(db, col);

        let querySnapshot;
        if (!q) {
          querySnapshot = await getDocs(colRef);
        } else {
          const queryObj = query(colRef, where(q[0], q[1], q[2]));
          querySnapshot = await getDocs(queryObj);
        }

        const docs = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          data.id = doc.id;
          docs.push(data);
        });

        return docs;
      } catch (error) {
        logger.error("Error leyendo documentos:", error);
        throw error;
      }
    },

    async countDocuments(col) {
      try {
        logger.debug("Contando documentos:", col);
        const collectionRef = collection(db, col);
        const snapshot = await getCountFromServer(query(collectionRef));
        const count = snapshot.data().count;
        logger.debug("Conteo:", count);
        return count;
      } catch (error) {
        logger.error("Error contando documentos:", error);
        throw error;
      }
    },

    /**
     * Get all users
     */
    async getAllUsers() {
      try {
        const usersRef = collection(db, "users");
        const snapshot = await getDocs(usersRef);
        const users = [];

        snapshot.forEach((docSnap) => {
          users.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });

        logger.debug("Fetched users:", users.length);
        return users;
      } catch (error) {
        logger.error("Error fetching users:", error);
        throw error;
      }
    },

    /**
     * Delete a user
     */
    async deleteUser(uid) {
      try {
        await deleteDoc(doc(db, "users", uid));
        logger.info("User deleted:", uid);
        return true;
      } catch (error) {
        logger.error("Error deleting user:", error);
        throw error;
      }
    },
  },
});
